import React from 'react';
import PetFeature from '../PetFeature';
import PetCard from '../PetCard';
import WatercolorWash from '../WatercolorWash';
import LoadingMascot from '../LoadingMascot';
import { useReveal } from '../../hooks/useReveal';
import type { Pet } from '../../types';
import '../../styles/Pets.css';

interface PetsProps {
  featured?: Pet[];
  catalog?: Pet[];
  total?: number;
  chapter?: string;
  catalogHead?: string;
  loading?: boolean;
  isEmpty?: boolean;
}

const NUMBER_WORDS: Record<number, string> = {
  1: 'um', 2: 'dois', 3: 'três', 4: 'quatro', 5: 'cinco',
  6: 'seis', 7: 'sete', 8: 'oito', 9: 'nove', 10: 'dez',
  11: 'onze', 12: 'doze', 13: 'treze', 14: 'catorze', 15: 'quinze',
  16: 'dezasseis', 17: 'dezassete', 18: 'dezoito', 19: 'dezanove', 20: 'vinte',
};

function numberWord(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}

const Pets: React.FC<PetsProps> = ({
  featured = [],
  catalog = [],
  total = 19,
  chapter = 'Cap. II · Quem espera',
  catalogHead = 'Também',
  loading = false,
  isEmpty = false,
}) => {
  const remaining = Math.max(0, total - featured.length - catalog.length);
  const showEntries = !loading && !isEmpty && (featured.length > 0 || catalog.length > 0);
  const { ref, revealed } = useReveal<HTMLElement>();

  return (
    <section
      ref={ref}
      className={`pets psl-reveal${revealed ? ' is-in-view' : ''}`}
      id="pets"
    >
      <WatercolorWash
        variant="b"
        color="#a14a2b"
        opacity={0.25}
        style={{ top: '120px', right: '-100px' }}
      />

      <div className="pets__header">
        <div>
          <div className="pets__chapter">{chapter}</div>
          <h2 className="pets__head">
            À espera de <em>casa</em><br />— e de nome ao&nbsp;telefone.
          </h2>
        </div>
        <p className="pets__note">
          Começamos com duas fichas abertas, depois o resto do caderno. Cada ficha é
          escrita à mão por quem cuida da rotina do animal. Queres conhecer ao vivo?
          Marca visita.
        </p>
      </div>

      {loading && (
        <LoadingMascot
          className="pets__placeholder"
          label="a folhear o caderno…"
        />
      )}

      {isEmpty && (
        <div className="pets__empty">
          <div className="pets__empty-kind">— uma nota</div>
          <p>
            O caderno está <em>vazio</em> neste momento. Volta amanhã —
            <br />ou diz olá pelas redes sociais e conta-nos a tua história.
          </p>
        </div>
      )}

      {showEntries && featured.length > 0 && (
        <div className="pets__featured">
          {featured.map((pet, i) => (
            <PetFeature key={pet.id} pet={pet} flip={i % 2 === 1} />
          ))}
        </div>
      )}

      {showEntries && catalog.length > 0 && (
        <>
          <div className="pets__catalog-head">
            <div className="pets__catalog-title">
              {catalogHead} <em>à espera</em>
            </div>
            {remaining > 0 && (
              <div className="pets__catalog-count">— mais {numberWord(remaining)} no caderno</div>
            )}
          </div>

          <div className="pets__catalog">
            {catalog.map((pet) => (
              <PetCard key={pet.id} pet={pet} />
            ))}
          </div>

          {remaining > 0 && (
            <p className="pets__allpets">
              <a href="/adotar/caes">Adotar um cão →</a>
              <span aria-hidden="true">  ·  </span>
              <a href="/adotar/gatos">Adotar uma gata →</a>
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default Pets;
